// Fully client-side audio export for the Studio. Separation runs in the browser
// (WebGPU/onnxruntime), so the stems already live here as blob URLs — we render
// and download locally with the Web Audio API. No server round-trip.
//
// Formats: WAV (native, lossless, no deps) and MP3 (lazy-loaded `lamejs`, only
// pulled when the user picks MP3 — zero weight on the initial Studio load).

export type ExportFormat = 'wav' | 'mp3' | 'm4a';
export const FORMAT_EXT: Record<ExportFormat, string> = { wav: 'wav', mp3: 'mp3', m4a: 'm4a' };
export const FORMAT_MIME: Record<ExportFormat, string> = { wav: 'audio/wav', mp3: 'audio/mpeg', m4a: 'audio/mp4' };

// M4A (AAC) needs a browser with WebCodecs AAC encoding (Chrome has it). Used to
// hide the M4A option where it isn't available so we never offer a dead export.
export async function m4aSupported(): Promise<boolean> {
  try {
    if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') return false;
    const { supported } = await AudioEncoder.isConfigSupported({
      codec: 'mp4a.40.2', sampleRate: 48000, numberOfChannels: 2, bitrate: 192000,
    });
    return !!supported;
  } catch {
    return false;
  }
}

type ACtor = typeof AudioContext;
function getAC(): ACtor {
  const w = window as unknown as { AudioContext: ACtor; webkitAudioContext?: ACtor };
  return w.AudioContext || w.webkitAudioContext!;
}

// Render the given stems at their effective gains to a single AudioBuffer. `gains`
// maps stem → effective gain (0 ⇒ skipped, so mutes/solos are honoured by the
// caller). Renders at the device sample rate (decodeAudioData resamples into it).
export async function renderMix(
  stemUrls: Record<string, string>,
  gains: Record<string, number>,
  // Optional [start,end] window in seconds — renders only that slice. Used by
  // the Studio's trim feature to keep a shorter version of a separated clip.
  window?: { start: number; end: number },
): Promise<AudioBuffer> {
  const Ctx = getAC();
  const decodeCtx = new Ctx();
  const sampleRate = decodeCtx.sampleRate;
  try {
    const active = Object.keys(gains).filter((s) => gains[s] > 0 && stemUrls[s]);
    const decoded = await Promise.all(
      active.map(async (stem) => {
        const ab = await fetch(stemUrls[stem]).then((r) => r.arrayBuffer());
        return { stem, buf: await decodeCtx.decodeAudioData(ab) };
      }),
    );
    if (!decoded.length) throw new Error('Nothing to export — every stem is silent.');
    const fullLen = Math.max(...decoded.map((d) => d.buf.length));
    const maxEnd = fullLen / sampleRate;
    const startSec = window ? Math.max(0, window.start) : 0;
    const endSec = window ? Math.min(maxEnd, Math.max(startSec, window.end)) : maxEnd;
    const length = window ? Math.max(1, Math.round((endSec - startSec) * sampleRate)) : fullLen;
    const off = new OfflineAudioContext(2, length, sampleRate);
    for (const { stem, buf } of decoded) {
      const src = off.createBufferSource();
      src.buffer = buf;
      const g = off.createGain();
      g.gain.value = gains[stem];
      src.connect(g).connect(off.destination);
      if (window) src.start(0, startSec, endSec - startSec);
      else src.start();
    }
    return off.startRendering();
  } finally {
    decodeCtx.close().catch(() => {});
  }
}

// Optional shaping for renderAndEncode. `window` renders only that slice (same
// semantics as renderMix's window); `fadeOutSec` ramps the tail of the rendered
// buffer to silence so a truncated export ends on a fade instead of a hard cut.
export interface RenderOptions {
  window?: { start: number; end: number };
  fadeOutSec?: number;
}

// Render the mix and encode it to the chosen format → downloadable Blob.
export async function renderAndEncode(
  stemUrls: Record<string, string>,
  gains: Record<string, number>,
  format: ExportFormat,
  opts?: RenderOptions,
): Promise<Blob> {
  const buf = await renderMix(stemUrls, gains, opts?.window);
  if (opts?.fadeOutSec && opts.fadeOutSec > 0) applyFadeOut(buf, opts.fadeOutSec);
  return encodeBuffer(buf, format);
}

// Encode an already-rendered buffer. Split out of renderAndEncode so callers
// that build their own audio (the click track) share the same encoders.
export async function encodeBuffer(buf: AudioBuffer, format: ExportFormat): Promise<Blob> {
  if (format === 'mp3') return encodeMp3(buf);
  if (format === 'm4a') return encodeM4a(buf);
  return audioBufferToWav(buf);
}

// The metronome as an exportable channel: the same blips the Studio plays
// (accent on beat one), rendered offline against the track's own tempo grid so
// the file lines up with the other stems in a DAW.
export async function renderClickTrack(opts: {
  durationSec: number;
  bpm: number;
  beatsPerBar: number;
  sampleRate?: number;
}): Promise<AudioBuffer> {
  const sr = opts.sampleRate || 48000;
  const dur = Math.max(0.1, opts.durationSec);
  const spb = 60 / Math.max(20, opts.bpm);
  const perBar = Math.max(1, Math.round(opts.beatsPerBar));
  const off = new OfflineAudioContext(2, Math.max(1, Math.round(dur * sr)), sr);
  for (let i = 0; i * spb < dur; i++) {
    const at = i * spb;
    const accent = i % perBar === 0;
    const osc = off.createOscillator();
    const g = off.createGain();
    osc.frequency.value = accent ? 1600 : 1000;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(accent ? 0.5 : 0.3, at + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
    osc.connect(g).connect(off.destination);
    osc.start(at);
    osc.stop(Math.min(dur, at + 0.06));
  }
  return off.startRendering();
}

// Linear in-place fade over the last `seconds` of the buffer.
function applyFadeOut(buf: AudioBuffer, seconds: number): void {
  const n = Math.min(buf.length, Math.max(1, Math.round(seconds * buf.sampleRate)));
  const start = buf.length - n;
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const ch = buf.getChannelData(c);
    for (let i = 0; i < n; i++) ch[start + i] *= 1 - i / n;
  }
}

// 16-bit PCM stereo WAV from an AudioBuffer — interleaved, no dependencies.
export function audioBufferToWav(buf: AudioBuffer): Blob {
  const numCh = Math.min(2, buf.numberOfChannels);
  const len = buf.length;
  const sampleRate = buf.sampleRate;
  const ch0 = buf.getChannelData(0);
  const ch1 = numCh > 1 ? buf.getChannelData(1) : ch0;
  const blockAlign = numCh * 2;
  const dataSize = len * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);
  const wStr = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  wStr(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); wStr(8, 'WAVE');
  wStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, numCh, true); view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  wStr(36, 'data'); view.setUint32(40, dataSize, true);
  let o = 44;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      const sample = Math.max(-1, Math.min(1, (c === 0 ? ch0 : ch1)[i]));
      view.setInt16(o, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      o += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

function floatTo16(f: Float32Array): Int16Array {
  const out = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

// MP3 via lazy-loaded lamejs (pure JS, no wasm). Only imported on first MP3 export.
export async function encodeMp3(buf: AudioBuffer, kbps = 256): Promise<Blob> {
  const lame = await import('@breezystack/lamejs');
  const Mp3Encoder = lame.Mp3Encoder ?? lame.default?.Mp3Encoder;
  const channels = Math.min(2, buf.numberOfChannels);
  const enc = new Mp3Encoder(channels, buf.sampleRate, kbps);
  const left = floatTo16(buf.getChannelData(0));
  const right = channels > 1 ? floatTo16(buf.getChannelData(1)) : left;
  const block = 1152;
  const chunks: BlobPart[] = [];
  for (let i = 0; i < left.length; i += block) {
    const l = left.subarray(i, i + block);
    const r = right.subarray(i, i + block);
    const mp3 = channels > 1 ? enc.encodeBuffer(l, r) : enc.encodeBuffer(l);
    if (mp3.length > 0) chunks.push(new Uint8Array(mp3));
  }
  const end = enc.flush();
  if (end.length > 0) chunks.push(new Uint8Array(end));
  return new Blob(chunks, { type: 'audio/mpeg' });
}

// M4A = AAC-LC (WebCodecs AudioEncoder, codec mp4a.40.2) muxed into an MP4
// container with the lazy-loaded, dependency-free mp4-muxer. Mirrors the
// AudioData feeding pattern used for the Opus stem cache (lib/opus.ts).
export async function encodeM4a(buf: AudioBuffer, bitrate = 192000): Promise<Blob> {
  if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') {
    throw new Error('M4A export needs a browser with WebCodecs (try Chrome).');
  }
  const channels = Math.min(2, buf.numberOfChannels);
  const sr = buf.sampleRate;
  const cfg: AudioEncoderConfig = { codec: 'mp4a.40.2', sampleRate: sr, numberOfChannels: channels, bitrate };
  const { supported } = await AudioEncoder.isConfigSupported(cfg);
  if (!supported) throw new Error('AAC encoding isn’t supported in this browser.');

  const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    audio: { codec: 'aac', sampleRate: sr, numberOfChannels: channels },
    fastStart: 'in-memory',
  });

  let encErr: unknown = null;
  let chunkCount = 0;
  const encoder = new AudioEncoder({
    output: (chunk, meta) => { chunkCount++; muxer.addAudioChunk(chunk, meta); },
    error: (e) => { encErr = e; },
  });
  encoder.configure(cfg);

  const frames = buf.length;
  const planar = new Float32Array(frames * channels);
  for (let c = 0; c < channels; c++) buf.copyFromChannel(planar.subarray(c * frames, (c + 1) * frames), c);
  const BLOCK = Math.floor(sr / 2);
  try {
    for (let off = 0; off < frames; off += BLOCK) {
      const n = Math.min(BLOCK, frames - off);
      const slice = new Float32Array(n * channels);
      for (let c = 0; c < channels; c++) slice.set(planar.subarray(c * frames + off, c * frames + off + n), c * n);
      const ad = new AudioData({
        format: 'f32-planar', sampleRate: sr, numberOfFrames: n, numberOfChannels: channels,
        timestamp: Math.round((off / sr) * 1e6), data: slice,
      });
      encoder.encode(ad);
      ad.close();
    }
    await encoder.flush();
  } finally {
    encoder.close(); // always release the encoder, even if encode/flush throws
  }
  if (encErr) throw encErr instanceof Error ? encErr : new Error('AAC encode failed.');
  if (chunkCount === 0) throw new Error('M4A export produced no audio.');

  muxer.finalize();
  const { buffer } = muxer.target as { buffer: ArrayBuffer };
  return new Blob([buffer], { type: 'audio/mp4' });
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Strip an extension + filesystem-unsafe characters so downloads name cleanly.
export function safeBaseName(raw: string, fallback = 'riffloop'): string {
  const base = (raw || '').replace(/\.[a-z0-9]{1,5}$/i, '').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  return base || fallback;
}
